import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Shield, Truck, Award, Fish, Gift, CreditCard, TrendingUp } from 'lucide-react';
import { getAllProducts, getAllCategories } from '../api/productAPI';
import ProductCard from '../components/ProductCard';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './HomePage.css';

const TYPING_WORDS = ['Seafood', 'Salmon', 'Lobster', 'Prawns', 'Squid', 'Tuna'];

const HomePage = () => {
  const [typedWord, setTypedWord] = useState('Seafood');
  const [wordIndex, setWordIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(7);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = TYPING_WORDS[wordIndex];
    const timeout = setTimeout(() => {
      if (!deleting) {
        if (charIndex < current.length) {
          setTypedWord(current.slice(0, charIndex + 1));
          setCharIndex(c => c + 1);
        } else {
          setTimeout(() => setDeleting(true), 1800);
        }
      } else {
        if (charIndex > 0) {
          setTypedWord(current.slice(0, charIndex - 1));
          setCharIndex(c => c - 1);
        } else {
          setDeleting(false);
          setWordIndex(i => (i + 1) % TYPING_WORDS.length);
        }
      }
    }, deleting ? 60 : 100);
    return () => clearTimeout(timeout);
  }, [charIndex, deleting, wordIndex]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        getAllProducts(),
        getAllCategories(),
      ]);
      if (productsRes.success) {
        setFeaturedProducts(productsRes.data.slice(0, 8));
      }
      if (categoriesRes.success) {
        // Only show top-level categories (no parent), sorted by sortOrder
        const topLevel = categoriesRes.data.filter(c => !c.parent);
        setCategories(topLevel);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />


      {/* Hero */}
      <section className="hero-fullscreen">
        <div className="hero-bg">
          {/* Replace this URL with your actual image */}
          <img
            src="https://i.ibb.co/TDFKkRkv/5.jpg"
            alt="Fresh seafood"
            className="hero-bg-img"
          />
          <div className="hero-overlay" />
        </div>
        <div className="hero-fs-content">
          <div className="hero-fs-inner">
            <div className="hero-fs-badge">
              Premium Seafood · Delivered Fresh in Ghana
            </div>
            <h1 className="hero-fs-title">
              Ocean Fresh<br />
              <span className="hero-fs-accent">{typedWord}<span className="typing-cursor">|</span></span> at Your Door
            </h1>
            <p className="hero-fs-subtitle">
              Ghana's premier seafood destination. The finest selection
              of fresh and frozen seafood — quality you can taste,
              service you can trust.
            </p>
            <div className="hero-fs-actions">
              <Link to="/shop" className="hero-fs-btn-primary">
                Shop Now <ArrowRight size={18} />
              </Link>
              <Link to="/register" className="hero-fs-btn-secondary">
                Join Loyalty Program
              </Link>
            </div>
            <div className="hero-fs-stats">
              <div className="hero-fs-stat">
                <span className="hero-fs-stat-num">500+</span>
                <span className="hero-fs-stat-label">Happy Customers</span>
              </div>
              <div className="hero-fs-stat-divider" />
              <div className="hero-fs-stat">
                <span className="hero-fs-stat-num">50+</span>
                <span className="hero-fs-stat-label">Products</span>
              </div>
              <div className="hero-fs-stat-divider" />
              <div className="hero-fs-stat">
                <span className="hero-fs-stat-num">100%</span>
                <span className="hero-fs-stat-label">Fresh & Quality</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Hero - Original Layout */}
      <section className="hero hero-mobile-original">
        <div className="hero-content">
          <div className="hero-badge">
            <Fish size={14} />
            Premium Seafood Delivered Fresh
          </div>
          <h1 className="hero-title">
            Ocean Fresh<br />
            <span className="hero-title-accent">Seafood</span>{' '}
            at Your Door
          </h1>
          <p className="hero-subtitle">
            Discover the finest selection of fresh seafood from Ghana's
            premier seafood destination. Quality you can taste,
            service you can trust.
          </p>
          <div className="hero-actions">
            <Link to="/shop" className="hero-btn-primary">
              Shop Now <ArrowRight size={18} />
            </Link>
            <Link to="/register" className="hero-btn-secondary">
              Join Loyalty Program
            </Link>
          </div>
          <div className="hero-stats">
            <div className="hero-stat">
              <span className="stat-number">500+</span>
              <span className="stat-label">Happy Customers</span>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <span className="stat-number">50+</span>
              <span className="stat-label">Products</span>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <span className="stat-number">100%</span>
              <span className="stat-label">Fresh & Quality</span>
            </div>
          </div>
        </div>
        <div className="hero-image">
          <div className="hero-image-circle">
            <Fish size={80} color="#29ABE2" strokeWidth={1} />
          </div>
          <div className="hero-float-card hero-float-card-1">
            <Gift size={20} color="#1565C0" />
            <div>
              <p className="float-title">Loyalty Rewards</p>
              <p className="float-sub">Earn points on every order</p>
            </div>
          </div>
          <div className="hero-float-card hero-float-card-2">
            <Truck size={20} color="#1565C0" />
            <div>
              <p className="float-title">Fast Delivery</p>
              <p className="float-sub">Same day delivery</p>
            </div>
          </div>
        </div>
      </section>
      {/* Features */}
      <section className="features-section">
        <div className="container">
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">
                <Truck size={24} />
              </div>
              <div>
                <h4>Fast Delivery</h4>
                <p>Same day delivery in Accra</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <Shield size={24} />
              </div>
              <div>
                <h4>Quality Guaranteed</h4>
                <p>Fresh from the source daily</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <Award size={24} />
              </div>
              <div>
                <h4>Loyalty Rewards</h4>
                <p>Earn points on every purchase</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <Star size={24} />
              </div>
              <div>
                <h4>Premium Selection</h4>
                <p>Economy, Business & First Class</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Shop by Category</h2>
              <p className="section-subtitle">
                Explore our wide range of fresh seafood categories
              </p>
            </div>
            <div className="categories-grid">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/shop?category=${category.id}`}
                  className="category-card"
                >
                  {category.imageUrl ? (
                    <img src={category.imageUrl} alt={category.name} />
                  ) : (
                    <div className="category-placeholder">
                      <Fish size={32} color="#29ABE2" />
                    </div>
                  )}
                  <div className="category-overlay">
                    <h3>{category.name}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Featured Products</h2>
            <p className="section-subtitle">
              Handpicked fresh seafood just for you
            </p>
          </div>

          {loading ? (
            <div className="loading-grid">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="product-skeleton" />
              ))}
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="products-grid">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Fish size={48} color="#29ABE2" strokeWidth={1} />
              <p>No products available yet. Check back soon!</p>
            </div>
          )}

          <div className="section-action">
            <Link to="/shop" className="btn-outline">
              View All Products <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Loyalty Banner */}
      <section className="loyalty-banner">
        <div className="container">
          <div className="loyalty-banner-content">
            <div className="loyalty-banner-text">
              <div className="loyalty-badge">
                <Award size={14} />
                Aurora Loyalty Program
              </div>
              <h2>Earn While You Shop</h2>
              <p>
                Join our loyalty program and earn Aurora Points on every purchase.
                Redeem points for discounts and enjoy exclusive member benefits.
              </p>
              <div className="loyalty-perks">
                <div className="perk">
                  <TrendingUp size={16} color="#1565C0" />
                  <span>GHS 1 spent = 1 Aurora Point</span>
                </div>
                <div className="perk">
                  <Gift size={16} color="#1565C0" />
                  <span>100 Points = GHS 1 discount</span>
                </div>
                <div className="perk">
                  <Award size={16} color="#1565C0" />
                  <span>Climb from Economy to First Class</span>
                </div>
                <div className="perk">
                  <CreditCard size={16} color="#1565C0" />
                  <span>Loyalty cards with up to 20% off</span>
                </div>
              </div>
              <Link to="/register" className="hero-btn-primary">
                Join Now — It's Free <ArrowRight size={18} />
              </Link>
            </div>
            <div className="loyalty-cards-preview">
              <div className="preview-card preview-card-premium">
                <div className="preview-card-header">
                  <span>Aurora</span>
                  <CreditCard size={20} />
                </div>
                <div className="preview-card-amount">GHS 200</div>
                <div className="preview-card-discount">20% OFF</div>
                <div className="preview-card-footer">
                  <span>First Class Member</span>
                </div>
              </div>
              <div className="preview-card preview-card-basic">
                <div className="preview-card-header">
                  <span>Aurora</span>
                  <CreditCard size={20} />
                </div>
                <div className="preview-card-amount">GHS 100</div>
                <div className="preview-card-discount">10% OFF</div>
                <div className="preview-card-footer">
                  <span>Economy Member</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default HomePage;